import { type TypedDocumentNode, gql } from '@apollo/client';
import { FormattedMessage } from 'react-intl';
import { type CSSProperties } from 'styled-components';

import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { BrandM, LabelM } from '@sorare/core/src/atoms/typography';
import { idFromObject } from '@sorare/core/src/gql/idFromObject';
import { isSpecialCardSet } from '@sorare/core/src/lib/cardSets';

import { usePackAssets } from 'components/commonCard/PacksTypePage/usePackAssets';

import {
  Grid,
  Header,
  Logo,
  MainPack,
  NeonEdge,
  SecondaryPack,
  SeriesCardPackGroupWrapper,
} from './ui';
import { PackWithBuyButton } from '../PackWithBuyButton';
import type { SeriesCardPackGroup_cardPackGroup } from './__generated__/index.graphql';
import bundesligaPackGroup from './assets/bundesliga-pack-group.webp';
import laligaPackGroup from './assets/laliga-pack-group.webp';
import ligue1PackGroup from './assets/ligue1-pack-group.webp';
import premierleaguePackGroup from './assets/premierleague-pack-group.webp';

const getBgSeriesImage = (cardPackGroupId: string) => {
  if (!cardPackGroupId) return null;
  if (cardPackGroupId.includes('premier-league')) return premierleaguePackGroup;
  if (cardPackGroupId.includes('bundesliga')) return bundesligaPackGroup;
  if (cardPackGroupId.includes('laliga')) return laligaPackGroup;
  if (cardPackGroupId.includes('ligue-1')) return ligue1PackGroup;
  return null;
};

type Pack = SeriesCardPackGroup_cardPackGroup['cardPacks'][number] | null;

type Result = {
  goat?: Pack;
  elite?: Pack;
  basic?: Pack;
};

type Props = {
  cardPackGroup: SeriesCardPackGroup_cardPackGroup;
  onClickBuy: (cardPackGroupId: string, cardPackSlug: string) => void;
};

export const SeriesCardPackGroup = ({ cardPackGroup, onClickBuy }: Props) => {
  const { goat, elite, basic } = cardPackGroup.cardPacks.reduce<Result>(
    (acc, pack) => {
      const { slug } = pack;
      if (slug.includes('goat')) {
        return { ...acc, goat: pack };
      }
      if (slug.includes('elite')) {
        return { ...acc, elite: pack };
      }
      if (slug.includes('basic')) {
        return { ...acc, basic: pack };
      }
      return acc;
    },
    { goat: null, elite: null, basic: null }
  );

  const { bgImage } = usePackAssets({
    cardPackGroupId: idFromObject(cardPackGroup.id),
  });

  const isSeries = isSpecialCardSet(cardPackGroup?.cardSet?.slug);

  if (!isSeries) return null;

  const bgSeriesImage = getBgSeriesImage(idFromObject(cardPackGroup.id));

  const backgroundImage = bgSeriesImage || bgImage;

  const color = cardPackGroup.cardPacks.find(
    pack => pack.primaryColor
  )?.primaryColor;
  return (
    <SeriesCardPackGroupWrapper
      style={
        {
          '--background-image': `url(${backgroundImage})`,
        } as CSSProperties
      }
    >
      <NeonEdge side="top" showNeon={'always'} color={color || ''}>
        <Header>
          <Logo src={cardPackGroup?.pictureUrl} alt={cardPackGroup.title} />
        </Header>
        <Grid>
          {!!goat && (
            <MainPack>
              <Vertical gap={0.5}>
                <BrandM>
                  <FormattedMessage
                    id="SeriesCardPackGroup.title"
                    defaultMessage="Star Pack"
                  />
                </BrandM>
                <LabelM>
                  <FormattedMessage
                    id="SeriesCardPackGroup.description"
                    defaultMessage="One Guaranteed Star Player!"
                  />
                </LabelM>
              </Vertical>
              <PackWithBuyButton
                cardPack={goat}
                cardPackGroupId={cardPackGroup.id}
                onClickBuy={onClickBuy}
                gap={3}
                isPremiumPack
              />
            </MainPack>
          )}

          {!!basic && (
            <SecondaryPack>
              <PackWithBuyButton
                cardPack={basic}
                cardPackGroupId={cardPackGroup.id}
                onClickBuy={onClickBuy}
                gap={3}
              />
            </SecondaryPack>
          )}
          {!!elite && (
            <SecondaryPack>
              <PackWithBuyButton
                cardPack={elite}
                cardPackGroupId={cardPackGroup.id}
                onClickBuy={onClickBuy}
                gap={3}
              />
            </SecondaryPack>
          )}
        </Grid>
      </NeonEdge>
    </SeriesCardPackGroupWrapper>
  );
};

SeriesCardPackGroup.fragments = {
  cardPackGroup: gql`
    fragment SeriesCardPackGroup_cardPackGroup on CardPackGroup {
      id
      title
      pictureUrl
      cardSet {
        slug
      }
      cardPacks {
        primaryColor: color(variant: PRIMARY)
        slug
        ...PackWithBuyButton_cardPack
      }
    }
    ${PackWithBuyButton.fragments.cardPack}
  ` as TypedDocumentNode<SeriesCardPackGroup_cardPackGroup>,
};
